import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_THEME = {
  primary: "#CCFF00",
  primary_dark: "#B3E600",
  bg: "#0A0A0A",
  surface: "#141414",
  border: "rgba(255,255,255,0.10)",
  text: "#FFFFFF",
  text_muted: "#9CA3AF",
  danger: "#EF4444",
  success: "#10B981",
};

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  defaultTheme: DEFAULT_THEME,
  refresh: () => {},
  apply: () => {},
  setClubOverride: () => {},
  clearClubOverride: () => {},
});

function applyToRoot(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--theme-primary", theme.primary);
  root.style.setProperty("--theme-primary-dark", theme.primary_dark);
  root.style.setProperty("--theme-bg", theme.bg);
  root.style.setProperty("--theme-surface", theme.surface);
  root.style.setProperty("--theme-border", theme.border);
  root.style.setProperty("--theme-text", theme.text);
  root.style.setProperty("--theme-text-muted", theme.text_muted);
  root.style.setProperty("--theme-danger", theme.danger);
  root.style.setProperty("--theme-success", theme.success);
}

export function ThemeProvider({ children }) {
  const [globalTheme, setGlobalTheme] = useState(DEFAULT_THEME);
  const [defaultTheme, setDefaultTheme] = useState(DEFAULT_THEME);
  const [clubOverride, setClubOverrideState] = useState(null); // current effective override
  const activeRef = useRef(DEFAULT_THEME);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/theme`, { headers: { "Cache-Control": "no-cache" } });
      const t = r.data?.theme || DEFAULT_THEME;
      setGlobalTheme(t);
      setDefaultTheme(r.data?.default || DEFAULT_THEME);
      if (!clubOverride) {
        activeRef.current = t;
        applyToRoot(t);
      }
    } catch {
      activeRef.current = DEFAULT_THEME;
      applyToRoot(DEFAULT_THEME);
    }
  }, [clubOverride]);

  useEffect(() => { refresh(); }, [refresh]);

  const apply = useCallback((newTheme) => {
    activeRef.current = newTheme;
    applyToRoot(newTheme);
    setGlobalTheme(newTheme);
  }, []);

  const setClubOverride = useCallback((clubTheme) => {
    setClubOverrideState(clubTheme);
    if (clubTheme) {
      activeRef.current = clubTheme;
      applyToRoot(clubTheme);
    }
  }, []);

  const clearClubOverride = useCallback(() => {
    setClubOverrideState(null);
    activeRef.current = globalTheme;
    applyToRoot(globalTheme);
  }, [globalTheme]);

  const theme = clubOverride || globalTheme;

  return (
    <ThemeContext.Provider value={{ theme, defaultTheme, refresh, apply, setClubOverride, clearClubOverride }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Hook to temporarily apply a club's theme while a component is mounted.
 * Use on Club Detail / Public Club / Club AR View pages.
 */
export function useClubTheme(clubId) {
  const { setClubOverride, clearClubOverride } = useContext(ThemeContext);
  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/theme/club/${clubId}`).then((r) => {
      if (cancelled) return;
      const t = r.data?.theme;
      const hasOverride = r.data?.has_override;
      if (t && hasOverride) setClubOverride(t);
    }).catch(() => {});
    return () => { cancelled = true; clearClubOverride(); };
  }, [clubId, setClubOverride, clearClubOverride]);
}
